// Tenantra Inc. - Professional Landing Page (Warm Sand + Pro Animations)
import { useState, useEffect, useRef } from 'react';
import { 
  Building2, Shield, FileText, Users, CheckCircle, ChevronDown, 
  Lock, XCircle, Handshake, BadgeCheck, Scale,
  TrendingUp, UserCheck, Home, Award, Volume2, VolumeX,
  Linkedin, Twitter, Instagram, Facebook, Youtube, Send, PlayCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { StatusTag } from '@/components/public/StatusTag';
import { PartnersSection } from '@/components/public/PartnersSection';
import { GhlChatWidget } from '@/components/public/GhlChatWidget';

const PRODUCT_LINKS = [
  { name: 'Rent Reporting', href: '/rent-reporting', desc: 'Rent payments reported to Equifax Canada' },
  { name: 'Rent Collection', href: '/rent-collection', desc: 'Bank debit and Interac, matched to the lease' },
  { name: 'Tenant Screening', href: '/screening', desc: 'ID verification and verified rent history', tag: 'Beta' },
];

const RESOURCE_LINKS = [
  { name: 'FAQ', href: '/faq' },
  { name: 'Whitepaper', href: '/whitepaper' },
  { name: 'Ecosystem & Roadmap', href: '/community' },
  { name: 'Careers', href: '/careers' },
  { name: 'Contact', href: '/contact' },
];

const NavDropdown = ({ label, items, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={testId} className="inline-flex items-center gap-1 text-sm font-medium text-white/85 hover:text-white transition-colors duration-300 py-4">
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full -mt-1 w-72 bg-white border border-stone-200 rounded-lg shadow-[0_12px_32px_-12px_rgba(15,23,42,0.25)] py-2" data-testid={`${testId}-menu`}>
          {items.map((it) => (
            <a key={it.href} href={it.href} className="flex items-start justify-between gap-3 px-4 py-2.5 hover:bg-stone-50 transition-colors">
              <span>
                <span className="block text-sm font-medium text-stone-900">{it.name}</span>
                {it.desc && <span className="block text-xs text-stone-500 mt-0.5">{it.desc}</span>}
              </span>
              {it.tag && <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500 border border-stone-300 rounded px-1.5 py-0.5">{it.tag}</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom hook for scroll-triggered animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// Animated counter with easing
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    
    let startTime;
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = easeOutQuart(progress);
      setCount(Math.floor(easedProgress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [whyMattersOpen, setWhyMattersOpen] = useState(false);
  
  // Scroll animation refs
  const [heroRef, heroVisible] = useScrollAnimation();
  const [problemRef, problemVisible] = useScrollAnimation();
  const [howItWorksRef, howItWorksVisible] = useScrollAnimation();
  const [productsRef, productsVisible] = useScrollAnimation();
  const [landlordsRef, landlordsVisible] = useScrollAnimation();
  const [tenantsRef, tenantsVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();
  const [reelRef, reelVisible] = useScrollAnimation();
  const reelVideoRef = useRef(null);
  const [reelMuted, setReelMuted] = useState(true);
  const toggleReelSound = () => {
    const v = reelVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setReelMuted(v.muted);
    if (!v.muted) { v.currentTime = 0; v.play().catch(() => {}); }
  };

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const prev = document.documentElement.style.backgroundColor;
    document.documentElement.style.backgroundColor = '#FAF8F5';
    return () => { document.documentElement.style.backgroundColor = prev; };
  }, []);

  return (
    <div className="pub-soft pub-blend min-h-screen bg-white text-stone-700 overflow-x-hidden">
      <GhlChatWidget />
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-teal-600 shadow-[0_2px_12px_-4px_rgba(13,148,136,0.5)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo size="md" dark data-testid="logo-link" />
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <NavDropdown label="Products" items={PRODUCT_LINKS} testId="nav-products" />
            {[
              { name: 'Why Tenantra', href: '#problem' },
              { name: 'How It Works', href: '#how-it-works' },
              { name: 'Landlords', href: '#landlords' },
              { name: 'Tenants', href: '#tenants' }
            ].map((item) => (
              <a 
                key={item.name}
                href={item.href}
                className="text-sm font-medium text-white/85 hover:text-white transition-all duration-300 relative group"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300 rounded-full" />
              </a>
            ))}
            <NavDropdown label="Resources" items={RESOURCE_LINKS} testId="nav-resources" />
          </nav>
          
          {/* Desktop CTAs */}
          <div className="hidden sm:flex items-center gap-3">
            <a href="/login" className="text-sm font-medium text-white/90 hover:text-white transition-colors duration-300 px-3 py-2" data-testid="login-btn">
              Log In
            </a>
            <a href="/waitlist" data-testid="nav-waitlist-btn">
              <Button variant="outline" className="h-9 px-5 bg-transparent border border-white/70 hover:bg-white/10 text-white text-sm font-semibold transition-colors duration-200 rounded-lg">
                Join Waitlist
              </Button>
            </a>
            <a href="/register" data-testid="register-btn">
              <Button className="h-9 px-5 bg-white hover:bg-teal-50 text-teal-700 text-sm font-semibold transition-colors duration-200 rounded-lg shadow-sm">
                Get Started
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-white hover:text-white/80 transition-colors" aria-label="Toggle menu" data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="relative w-5 h-5">
              <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'top-2 rotate-45' : 'top-1'}`} />
              <span className={`absolute left-0 top-2 w-5 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
              <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'top-2 -rotate-45' : 'top-3'}`} />
            </div>
          </button>
        </div>

      </header>

      {/* Mobile Menu — rendered only while open, outside the header so no hidden panel ever affects layout */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-x-0 top-14 bottom-0 z-40 bg-white border-t border-teal-700/30 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="mobile-menu-panel"
        >
          <div className="px-4 py-5 space-y-1">
            <p className="px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">Products</p>
            {PRODUCT_LINKS.map((item) => (
              <a key={item.href} href={item.href} className="flex items-center justify-between text-base text-stone-700 hover:text-teal-700 hover:bg-teal-50 px-3 py-3 rounded-lg transition-colors duration-200" data-testid={`mobile-nav-${item.href.slice(1)}`}>
                {item.name}
                {item.tag && <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500">{item.tag}</span>}
              </a>
            ))}
            <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">Overview</p>
            {[
              { name: 'Why Tenantra', href: '#problem' },
              { name: 'How It Works', href: '#how-it-works' },
              { name: 'Landlords', href: '#landlords' },
              { name: 'Tenants', href: '#tenants' }
            ].map((item) => (
              <a 
                key={item.name}
                href={item.href} 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base text-stone-700 hover:text-teal-700 hover:bg-teal-50 px-3 py-3 rounded-lg transition-colors duration-200"
                data-testid={`mobile-nav-${item.href.slice(1)}`}
              >
                {item.name}
              </a>
            ))}
            <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">Resources</p>
            {RESOURCE_LINKS.map((item) => (
              <a key={item.href} href={item.href} className="block text-base text-stone-700 hover:text-teal-700 hover:bg-teal-50 px-3 py-3 rounded-lg transition-colors duration-200" data-testid={`mobile-nav-${item.href.slice(1)}`}>
                {item.name}
              </a>
            ))}
            <div className="pt-4 mt-3 border-t border-stone-200 grid grid-cols-2 gap-3">
              <a href="/login" className="h-11 inline-flex items-center justify-center rounded-lg border border-stone-300 bg-white text-sm font-medium text-stone-800" data-testid="mobile-login-btn">Log In</a>
              <a href="/waitlist" className="h-11 inline-flex items-center justify-center rounded-lg border border-teal-600 bg-white text-sm font-medium text-teal-700" data-testid="mobile-waitlist-btn">Join Waitlist</a>
              <a href="/register" className="h-11 inline-flex items-center justify-center rounded-lg bg-teal-600 text-sm font-medium text-white" data-testid="mobile-register-btn">Get Started</a>
            </div>
          </div>
        </div>
      )}

      {/* ==================== HERO SECTION ==================== */}
      <section 
        ref={heroRef}
        className="pub-hero relative pt-28 pb-20 sm:pt-36 sm:pb-28 overflow-hidden"
      >
        {/* Clean background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5] via-[#F5F0EA] to-[#EDE7DE]" />
        
        {/* Hero lifestyle photo — covers left ~60%, hard edge */}
        <div className="absolute top-0 bottom-0 left-0 w-full md:w-[60%] pointer-events-none overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1626275418623-0df0f07ce59c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
          />
        </div>
        
        {/* Subtle accent shape on right side */}
        <div className="absolute top-16 right-0 w-[400px] h-[400px] bg-teal-100/20 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              {/* Eyebrow */}
              <p 
                className={`text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-4 sm:mb-5 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                Canada's Rental History Platform
              </p>
              
              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4 sm:mb-5 text-stone-900">
                {['Your', 'Rental', 'Reputation'].map((word, i) => (
                  <span 
                    key={i}
                    className={`inline-block mr-2 sm:mr-3 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: `${150 + i * 100}ms` }}
                  >
                    {word}
                  </span>
                ))}
                <br />
                <span 
                  className={`inline-block text-teal-600 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                  style={{ transitionDelay: '450ms' }}
                >
                  Finally Matters
                </span>
              </h1>
              
              {/* Subheadline */}
              <p 
                className={`text-base lg:text-lg text-stone-700 leading-relaxed mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '550ms' }}
              >
                Good tenants get recognized. Bad history gets reported. 
                The transparent rental platform that benefits everyone.
              </p>
              
              {/* CTAs */}
              <div 
                className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 justify-center lg:justify-start transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '650ms' }}
              >
                <a href="/register" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto h-12 px-7 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold shadow-md transition-all duration-200 rounded-lg" data-testid="hero-cta-landlord">
                    <Building2 className="w-4 h-4 mr-2" />
                    I'm a Landlord
                  </Button>
                </a>
                <a href="/tenant/access" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto h-12 px-7 border-2 border-stone-300 bg-white hover:bg-stone-50 text-stone-700 hover:text-stone-900 text-sm font-semibold transition-all duration-200 rounded-lg" data-testid="hero-cta-tenant">
                    <FileText className="w-4 h-4 mr-2" />
                    View My Report
                  </Button>
                </a>
              </div>

              {/* Watch demo — its own line, tertiary action */}
              <div
                className={`mb-6 sm:mb-8 -mt-2 sm:-mt-3 flex justify-center lg:justify-start transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '700ms' }}
              >
                <a href="/demo" className="group inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors whitespace-nowrap" data-testid="hero-cta-demo">
                  <PlayCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Watch demo video
                </a>
              </div>
              
              {/* Trust indicators */}
              <div 
                className={`flex flex-wrap gap-5 sm:gap-6 justify-center lg:justify-start transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{ transitionDelay: '750ms' }}
              >
                {[
                  { icon: Shield, text: 'Bank-Level Security' },
                  { icon: BadgeCheck, text: 'PIPEDA Compliant' },
                  { icon: Lock, text: '256-bit Encryption' },
                ].map((item, i) => (
                  <span key={i} className="flex items-center gap-2 text-xs sm:text-sm text-stone-500">
                    <item.icon className="w-4 h-4 text-teal-600/70" />
                    {item.text}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Right - Hero Visual */}
            <div 
              className={`relative hidden lg:block transition-[opacity,transform] duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
              style={{ transitionDelay: '400ms' }}
            >
              {/* Main card */}
              <div className="relative bg-white rounded-2xl p-6 border border-stone-200/80 shadow-lg shadow-stone-200/50 overflow-hidden">
                
                {/* Score display */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-sm text-stone-500 mb-1">Tenant Score</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl font-bold text-teal-600">
                        <AnimatedCounter end={85} />
                      </span>
                      <span className="text-lg text-stone-400">/100</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-700">Excellent</span>
                  </div>
                </div>
                
                {/* Score breakdown */}
                <div className="space-y-3 mb-5">
                  {[
                    { label: 'Payment History', score: 38, max: 40, delay: 400 },
                    { label: 'Lease Compliance', score: 28, max: 30, delay: 600 },
                    { label: 'Property Care', score: 19, max: 30, delay: 800 },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-stone-700">{item.label}</span>
                        <span className="text-stone-700 font-medium">{item.score}/{item.max}</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-500 rounded-full transition-all duration-1000 ease-out"
                          style={{
                            width: heroVisible ? `${(item.score/item.max)*100}%` : '0%',
                            transitionDelay: `${item.delay}ms`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Verified tenancies */}
                <div className="pt-5 border-t border-stone-200">
                  <p className="text-sm text-stone-500 mb-3">Verified Rental History</p>
                  <div className="space-y-2">
                    {[
                      { address: '456 Bay Street, Toronto', dates: '2022 - Present', status: 'Active' },
                      { address: '123 Main Ave, Vancouver', dates: '2019 - 2022', status: 'Completed' },
                    ].map((item, i) => (
                      <div 
                        key={i} 
                        className={`flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-100 hover:border-stone-200 hover:bg-stone-100/50 transition-[opacity,transform,background-color,border-color] duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                        style={{ transitionDelay: `${1000 + i * 150}ms` }}
                      >
                        <div className="flex items-center gap-3">
                          <Home className="w-4 h-4 text-teal-600" strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-stone-700">{item.address}</p>
                            <p className="text-xs text-stone-500">{item.dates}</p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          item.status === 'Active' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-stone-200 text-stone-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PRODUCT REEL ==================== */}
      <section
        ref={reelRef}
        className="relative py-20 sm:py-28 bg-stone-950 overflow-hidden"
        data-testid="landing-reel-section"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left copy */}
            <div className={`text-center lg:text-left transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${reelVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400 mb-4">See it in action</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] text-white mb-5">
                One connected rental workflow
              </h2>
              <p className="text-base lg:text-lg text-stone-300 leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
                Less gut feel. More history. Watch how Tenantra screens applicants, collects rent, and reports it to Equifax Canada, all in under 30 seconds.
              </p>
              <div className="space-y-3 mb-8 max-w-sm mx-auto lg:mx-0">
                {[
                  { icon: UserCheck, label: 'Screen', note: 'Verified rent history, ID & liveness checks' },
                  { icon: FileText, label: 'Collect', note: 'PAD & Interac e-Transfer rent collection' },
                  { icon: TrendingUp, label: 'Report', note: 'On-time, late & missed rent to Equifax Canada' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-3 text-left">
                    <span className="w-9 h-9 rounded-lg bg-teal-500/15 border border-teal-500/25 flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-4 h-4 text-teal-400" strokeWidth={1.75} />
                    </span>
                    <p className="text-sm text-stone-300"><span className="font-semibold text-white">{s.label}.</span> {s.note}</p>
                  </div>
                ))}
              </div>
              <a href="/register" data-testid="reel-cta">
                <Button className="h-12 px-7 bg-teal-500 hover:bg-teal-400 text-stone-950 text-sm font-semibold rounded-lg transition-all duration-200">
                  Join Tenantra Today
                </Button>
              </a>
            </div>
            {/* Right - phone frame with reel */}
            <div className={`flex justify-center lg:justify-end transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${reelVisible ? 'opacity-100 translate-y-0 lg:translate-x-0' : 'opacity-0 translate-y-6 lg:translate-x-8'}`} style={{ transitionDelay: '150ms' }}>
              <div className="relative w-[260px] sm:w-[300px] rounded-[2.5rem] bg-stone-900 border border-stone-700/70 p-2.5 shadow-2xl shadow-black/40">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-stone-900 rounded-b-2xl z-10" />
                <div className="relative rounded-[2rem] overflow-hidden bg-black aspect-[9/16]">
                  <video
                    ref={reelVideoRef}
                    src="/tenantra-reel.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onClick={toggleReelSound}
                    className="w-full h-full object-cover cursor-pointer"
                    data-testid="landing-reel-video"
                  />
                  <button
                    type="button"
                    onClick={toggleReelSound}
                    aria-label={reelMuted ? 'Unmute video' : 'Mute video'}
                    className="absolute bottom-3 right-3 z-20 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-colors duration-200"
                    data-testid="reel-sound-toggle"
                  >
                    {reelMuted
                      ? <VolumeX className="w-4 h-4 text-white" strokeWidth={1.75} />
                      : <Volume2 className="w-4 h-4 text-white" strokeWidth={1.75} />}
                  </button>
                  {reelMuted && (
                    <div className="absolute bottom-3 left-3 z-20 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 pointer-events-none">
                      <span className="text-[11px] font-medium text-white">Tap for sound</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROBLEM SECTION ==================== */}
      <section 
        id="problem" 
        ref={problemRef}
        className="py-12 sm:py-16 lg:py-20 bg-[#FAF8F5]"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-8 sm:mb-10 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${problemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-600 mb-3 sm:mb-4">The Problem</p>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
              Canadian Rental Screening is <span className="text-red-500">Broken</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-stone-700 max-w-2xl mx-auto">
              Both landlords and tenants suffer from a system built on gut feelings and unverifiable claims.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {/* Landlords problems */}
            <div 
              className={`bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 shadow-sm transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${problemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                <h3 className="text-base sm:text-lg font-bold text-stone-800">For Landlords</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Fake references and unverifiable stories',
                  'No way to check history across properties',
                  'Bad tenants disappear and repeat the cycle',
                  'Risk thousands on gut feeling alone'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Tenants problems */}
            <div 
              className={`bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 shadow-sm transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${problemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '350ms' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <h3 className="text-base sm:text-lg font-bold text-stone-800">For Tenants</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Years of on-time rent = zero proof',
                  'One biased landlord can tank your chances',
                  'No portable "rental resume" exists',
                  'Good behaviour goes completely unrewarded'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Transition */}
          <div 
            className={`text-center mt-8 sm:mt-12 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${problemVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '500ms' }}
          >
            <div className="inline-flex items-center gap-2 sm:gap-3">
              <div className="h-px w-10 sm:w-16 bg-teal-400/50" />
              <span className="text-sm sm:text-base font-semibold text-teal-700">
                Tenantra changes everything
              </span>
              <div className="h-px w-10 sm:w-16 bg-teal-400/50" />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section 
        id="how-it-works" 
        ref={howItWorksRef}
        className="py-12 sm:py-16 lg:py-20 bg-white"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-8 sm:mb-10 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${howItWorksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3 sm:mb-4">How It Works</p>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
              Simple. Transparent. <span className="text-teal-600">Fair.</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-stone-700 max-w-2xl mx-auto">
              Tenantra is a rental credit platform that allows verified landlords to report tenant rental behaviour, 
              and allows tenants to build and access their rental history.
            </p>
          </div>
          
          {/* Explanation Box */}
          <div 
            className={`bg-white border border-stone-200 rounded-2xl mb-8 sm:mb-10 max-w-3xl mx-auto overflow-hidden shadow-sm transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${howItWorksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '150ms' }}
          >
            <button 
              onClick={() => setWhyMattersOpen(!whyMattersOpen)}
              className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-stone-50 transition-colors duration-300"
            >
              <h3 className="text-sm sm:text-base font-semibold text-stone-800">Why does this matter?</h3>
              <ChevronDown className={`w-4 sm:w-5 h-4 sm:h-5 text-stone-500 transition-transform duration-[400ms] ${whyMattersOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-[400ms] ease-out ${whyMattersOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3 sm:space-y-4 text-xs sm:text-sm text-stone-700 border-t border-stone-100 pt-3 sm:pt-4">
                <p>
                  <strong className="text-stone-800">For Landlords:</strong> Today, landlords rely on gut instincts, unverifiable references, 
                  and hope when selecting tenants. Tenantra solves this by creating a verifiable record of tenant behaviour that landlords can trust.
                </p>
                <p>
                  <strong className="text-stone-800">For Tenants:</strong> Good tenants have no way to prove their track record. 
                  Tenantra gives responsible tenants a way to build and showcase their rental reputation.
                </p>
                <p>
                  <strong className="text-stone-800">For the Rental Market:</strong> When both parties have access to verified information, 
                  trust increases, disputes decrease, and the rental process becomes fairer for everyone.
                </p>
              </div>
            </div>
          </div>
          
          {/* Steps */}
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {[
              {
                step: '01',
                icon: Shield,
                title: 'Landlords Get Verified',
                description: 'Verify identity and property ownership. Only verified landlords can submit reports.',
                delay: 250
              },
              {
                step: '02',
                icon: FileText,
                title: 'Reports Are Submitted',
                description: 'Report payment history, lease compliance, property care, and any issues.',
                delay: 400
              },
              {
                step: '03',
                icon: Handshake,
                title: 'Both Parties Benefit',
                description: 'Landlords screen with confidence. Tenants build a portable rental reputation.',
                delay: 550
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className={`bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-all duration-300 ${howItWorksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${item.delay}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl sm:text-3xl font-bold text-stone-200">{item.step}</div>
                  <item.icon className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm sm:text-base font-bold mb-2 text-stone-800">{item.title}</h3>
                <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnersSection />

      {/* ==================== PRODUCTS ==================== */}
      <section
        id="products"
        ref={productsRef}
        className="py-12 sm:py-16 lg:py-20 bg-[#FAF8F5] border-t border-stone-200"
        data-testid="products-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-8 sm:mb-10 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${productsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3 sm:mb-4">What Tenantra Does</p>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
              Screen. Collect. <span className="text-teal-600">Report.</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-stone-700 max-w-2xl mx-auto">
              Rent payments reported to Equifax Canada with the tenant's e-signed consent, plus the tools around it to screen applicants and collect rent.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                icon: TrendingUp, name: 'Rent Reporting', href: '/rent-reporting', tag: 'live',
                desc: 'Every month of rent, whether on time, late or missed, reported to Equifax Canada so it counts.',
                points: ['Starts only after the tenant e-signs consent', 'Tenant can view, dispute and withdraw anytime', 'Late and missed months reported as they happen'],
              },
              {
                icon: Building2, name: 'Rent Collection', href: '/rent-collection', tag: 'live',
                desc: 'Tenants pay by bank debit or Interac, matched to the lease and reported to Equifax once cleared.',
                points: ['Pre-authorized debit from any Canadian bank', 'Interac e‑Transfer requests to the tenant', 'NSF returns recorded, never marked paid'],
              },
              {
                icon: UserCheck, name: 'Tenant Screening', href: '/screening', tag: 'live',
                desc: 'One application link per property. Verify each applicant\'s ID and see their verified rent history.',
                points: ['Government ID and liveness check', 'Rent history from verified landlords', 'Equifax credit report (coming soon)'],
              },
            ].map((p, i) => (
              <a
                key={p.name}
                href={p.href}
                data-testid={`product-card-${p.href.slice(1)}`}
                className={`group flex flex-col bg-white border border-stone-200 rounded-2xl p-6 sm:p-7 shadow-sm hover:border-teal-600 hover:shadow-md transition-[opacity,transform,border-color,box-shadow] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${productsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-teal-700" strokeWidth={1.75} />
                  </div>
                  <StatusTag kind={p.tag} label={p.tagLabel} />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">{p.name}</h3>
                <p className="text-sm text-stone-600 leading-relaxed mb-4">{p.desc}</p>
                <ul className="space-y-2 mb-5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm text-stone-700">
                      <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />{pt}
                    </li>
                  ))}
                </ul>
                <span className="mt-auto text-sm font-semibold text-teal-700 group-hover:text-teal-800">Learn more →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FOR LANDLORDS ==================== */}
      <section 
        id="landlords" 
        ref={landlordsRef}
        className="relative py-12 sm:py-16 lg:py-20 bg-[#FAF8F5] overflow-hidden"
      >
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            <div className={`text-center lg:text-left transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${landlordsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3 sm:mb-4">For Landlords</p>
              
              <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
                Report with <span className="text-teal-600">Confidence</span>
              </h2>
              
              <p className="text-sm sm:text-base lg:text-lg text-stone-700 mb-5 sm:mb-6">
                Finally share and access real rental history. Protect your investment with verified data.
              </p>
              
              <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6">
                {[
                  { num: '1', title: 'Verify Your Identity', desc: 'One-time ID verification + property ownership documents' },
                  { num: '2', title: 'Add Properties', desc: 'Register rentals with tax documents or title proof' },
                  { num: '3', title: 'Submit Reports', desc: 'Report payment history, lease compliance, and issues' }
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex gap-3 sm:gap-4 text-left group transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${landlordsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                    style={{ transitionDelay: `${300 + i * 150}ms` }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{item.num}</span>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-semibold text-stone-800">{item.title}</h4>
                      <p className="text-xs sm:text-sm text-stone-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <a href="/register" className="inline-block">
                <Button className="h-11 px-6 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors duration-200 rounded-lg">
                  Register as Landlord
                </Button>
              </a>
            </div>
            
            {/* Visual */}
            <div 
              className={`relative hidden lg:block transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${landlordsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="bg-white rounded-2xl border border-stone-200/80 p-5 sm:p-6 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.18)]">
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-stone-100">
                  <CheckCircle className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm sm:text-base font-semibold text-stone-800">Landlord Verified</p>
                    <p className="text-xs sm:text-sm text-stone-500">All verification steps complete</p>
                  </div>
                </div>
                
                <div className="space-y-2.5">
                  {['Identity Verified', 'Property Tax Document', 'Ownership Confirmed', 'Phone Number Verified'].map((item, i) => (
                    <div 
                      key={i} 
                      className={`flex items-center gap-3 p-3 bg-stone-50 rounded-xl transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${landlordsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                      style={{ transitionDelay: `${500 + i * 100}ms` }}
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.75} />
                      <span className="text-sm text-stone-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 bg-white/60 rounded-2xl border border-stone-200 p-5 sm:p-6" data-testid="landlord-verification-note">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-2">What verification covers</p>
                <p className="text-sm text-stone-700 leading-relaxed">
                  Every landlord confirms their identity with government-issued ID and a Canadian phone number, then proves ownership or management authority for each property with a tax bill, title, or management agreement. Reports can only be submitted for verified properties, and each report displays the landlord's verification status to the tenant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FOR TENANTS ==================== */}
      <section 
        id="tenants" 
        ref={tenantsRef}
        className="py-12 sm:py-16 lg:py-20 bg-white"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Visual */}
            <div 
              className={`relative order-2 lg:order-1 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${tenantsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
              style={{ transitionDelay: '300ms' }}
            >
              {/* Inline photo — blended, visible on all screens */}
              <div className="mb-4 rounded-2xl overflow-hidden shadow-sm">
                <img 
                  src="https://images.unsplash.com/photo-1758523669073-edfbea249144?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Tenants reviewing their rental report"
                  className="w-full h-40 sm:h-48 lg:h-52 object-cover"
                />
              </div>
              
              <div className="bg-white rounded-2xl border border-stone-200/80 p-5 sm:p-6 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.18)]">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-xs sm:text-sm text-stone-500 mb-0.5 sm:mb-1">Your Tenant Score</p>
                    <p className="text-2xl sm:text-3xl font-bold text-cyan-600">
                      <AnimatedCounter end={85} />
                    </p>
                  </div>
                  <div className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                    <span className="text-xs sm:text-sm font-semibold text-emerald-700">Excellent</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: 'On-Time', value: 47 },
                    { label: 'Leases', value: 3 },
                    { label: 'Issues', value: 0 }
                  ].map((item, i) => (
                    <div key={i} className="text-center p-3 bg-stone-50 rounded-xl border border-stone-100">
                      <p className="text-lg sm:text-xl font-bold text-stone-800">
                        <AnimatedCounter end={item.value} duration={1200} />
                      </p>
                      <p className="text-[10px] sm:text-xs text-stone-500">{item.label}</p>
                    </div>
                  ))}
                </div>
                
                <div className="p-4 bg-cyan-50 rounded-xl border border-cyan-200" data-testid="tenant-score-basis">
                  <div className="flex items-center gap-2 mb-1">
                    <BadgeCheck className="w-4 h-4 text-cyan-600" />
                    <span className="text-sm font-semibold text-cyan-700">Verified rent history</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-stone-700">Based on 47 verified monthly reports from 3 landlords</p>
                </div>
              </div>
            </div>
            
            <div 
              className={`order-1 lg:order-2 text-center lg:text-left transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${tenantsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 mb-3 sm:mb-4">For Tenants</p>
              
              <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
                Build Your <span className="text-cyan-600">Reputation</span>
              </h2>
              
              <p className="text-sm sm:text-base lg:text-lg text-stone-700 mb-5 sm:mb-6">
                Your good rental history finally counts. Build a portable record that makes you stand out.
              </p>
              
              <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6">
                {[
                  { num: '1', title: 'Access Your Report', desc: 'Sign in with your email and password' },
                  { num: '2', title: 'View Your Score', desc: 'See your Tenant Behaviour Score and full history' },
                  { num: '3', title: 'Dispute If Needed', desc: 'Challenge errors with evidence and we investigate fairly' }
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex gap-3 sm:gap-4 text-left group transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${tenantsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                    style={{ transitionDelay: `${300 + i * 150}ms` }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{item.num}</span>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-base font-semibold text-stone-800">{item.title}</h4>
                      <p className="text-xs sm:text-sm text-stone-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <a href="/tenant/access" className="inline-block">
                <Button variant="outline" className="h-11 px-6 border-2 border-cyan-300 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-sm font-medium transition-colors duration-200 rounded-lg">
                  View My Rent Report
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES GRID ==================== */}
      <section 
        ref={featuresRef}
        className="py-12 sm:py-16 lg:py-20 bg-[#FAF8F5]"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-8 sm:mb-10 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-3 sm:mb-4">Standards</p>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 text-stone-900">
              Built for <span className="text-teal-600">Trust</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-stone-700 max-w-2xl mx-auto" data-testid="trust-intro">
              A rental record is only useful if both sides can rely on it. Every record on Tenantra is held to the same four rules: who can report, how they report, how disagreements are handled, and how the score is calculated.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              { icon: UserCheck, title: 'Verified Landlords', desc: 'Government ID + property ownership proof required', iconColor: 'text-teal-600' },
              { icon: FileText, title: 'Standardized Reports', desc: 'Consistent, objective data across all landlords', iconColor: 'text-cyan-600' },
              { icon: Scale, title: 'Fair Disputes', desc: 'Evidence-based review process for accuracy', iconColor: 'text-blue-600' },
              { icon: Award, title: 'Clear Scoring', desc: 'Transparent Excellent/Good/Fair/Poor ratings', iconColor: 'text-violet-600' }
            ].map((item, i) => (
              <div 
                key={i} 
                className={`bg-white border border-stone-200 rounded-2xl p-4 sm:p-5 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.18)] hover:shadow-[0_14px_40px_-12px_rgba(15,23,42,0.25)] transition-[opacity,transform,box-shadow] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <item.icon className={`w-6 h-6 ${item.iconColor} mb-4`} strokeWidth={1.5} />
                <h3 className="text-sm font-bold mb-2 text-stone-800">{item.title}</h3>
                <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div 
            className={`mt-8 sm:mt-10 grid sm:grid-cols-3 gap-4 sm:gap-6 border-t border-stone-400/40 pt-6 sm:pt-8 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '360ms' }}
            data-testid="trust-details"
          >
            {[
              ['Score formula', 'Payment history 40 points, lease compliance 30, property care 30. The same weights apply to every tenant, and each tenant can see how their number was derived.'],
              ['Disputes', 'A tenant can contest any entry with evidence. The entry is flagged while under review and the outcome is recorded alongside it.'],
              ['Privacy', 'Records are only visible to the tenant they concern and to verified landlords with the tenant\'s consent. Tenantra operates under PIPEDA.']
            ].map(([title, text]) => (
              <div key={title}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 mb-2">{title}</p>
                <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section 
        ref={ctaRef}
        className="relative py-12 sm:py-16 lg:py-20 bg-white overflow-hidden"
      >
        {/* Backdrop photo — residential community */}
        <div className="absolute inset-0 pointer-events-none">
          <img 
            src="https://images.unsplash.com/photo-1759345776191-ecc415822936?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400"
            alt=""
            className="w-full h-full object-cover object-center opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#E8E0D5] via-[#E8E0D5]/60 to-[#E8E0D5]/80" />
        </div>

        <div 
          className={`relative max-w-5xl mx-auto px-4 sm:px-6 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-3 sm:mb-4 text-stone-900">
              Ready to Build Trust in <span className="text-teal-600">Canadian Rentals?</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-stone-700 mb-6 sm:mb-8 max-w-xl mx-auto">
              Join the movement toward transparent, fair rental markets. Whether you're a landlord or tenant, your reputation matters.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10" data-testid="cta-pathways">
            {[
              { title: 'For Landlords', color: 'text-teal-700', points: ['Verify your identity and property ownership once', 'Invite team members under a single verified account', 'Report and review tenancies with standardized fields'] },
              { title: 'For Tenants', color: 'text-cyan-700', points: ['Sign in with your email and password', 'Review every entry on your record', 'Dispute anything inaccurate with evidence'] }
            ].map((col) => (
              <div key={col.title} className="bg-white/70 border border-stone-200 rounded-2xl p-5 sm:p-6 text-left">
                <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${col.color} mb-3`}>{col.title}</p>
                <ul className="space-y-2.5">
                  {col.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-stone-700">
                      <CheckCircle className={`w-4 h-4 ${col.color} flex-shrink-0 mt-0.5`} strokeWidth={1.75} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <a href="/register" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 bg-teal-600 hover:bg-teal-700 text-white text-base sm:text-lg font-semibold shadow-md transition-colors duration-200 rounded-lg" data-testid="cta-join-btn">
                Join Tenantra Today
              </Button>
            </a>
            <a href="/tenant/access" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 border-2 border-stone-300 bg-white hover:bg-stone-50 text-stone-700 text-base sm:text-lg font-semibold transition-colors duration-200 rounded-lg" data-testid="cta-tenant-btn">
                View My Report
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-12 sm:py-16 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 sm:gap-10 mb-10 sm:mb-12">
            <div className="col-span-2 md:col-span-1">
              <Logo size="md" className="mb-4" data-testid="footer-logo" />
              <p className="text-sm text-slate-600 leading-relaxed max-w-[220px]">
                Canada's rental behaviour bureau.
              </p>
            </div>
            
            {[
              { title: 'For Landlords', links: [
                { label: 'Rent Reporting', href: '/rent-reporting' },
                { label: 'Rent Collection', href: '/rent-collection' },
                { label: 'Tenant Screening', href: '/screening' },
                { label: 'Register', href: '/register' },
                { label: 'How It Works', href: '/#how-it-works' },
                { label: 'Pricing', href: '/#pricing' }
              ]},
              { title: 'For Tenants', links: [
                { label: 'View Report', href: '/tenant/access' },
                { label: 'Dispute Process', href: '/faq' },
                { label: 'FAQs', href: '/faq' }
              ]},
              { title: 'Community', links: [
                { label: 'Ecosystem & Roadmap', href: '/community' },
                { label: 'Whitepaper', href: '/whitepaper' },
                { label: 'Play Aviator Game', href: 'https://t.me/teaborabora_bot/tentgame', external: true },
                { label: 'Join Telegram', href: 'https://t.me/tenantra', external: true },
                { label: 'Careers', href: '/careers' },
                { label: 'Contact', href: '/contact' }
              ]},
              { title: 'Legal', links: [
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Data Protection', href: '/data-protection' }
              ]}
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-4 text-slate-500">{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map((link, j) => (
                    <li key={j}>
                      <a 
                        href={link.href} 
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className="inline-block py-1.5 sm:py-0 text-sm text-slate-800 hover:text-teal-700 transition-colors duration-200"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-slate-500">© {new Date().getFullYear()} Tenantra Inc. All rights reserved.</p>
            <div className="flex items-center gap-5" data-testid="footer-social-row">
              {[
                { name: 'LinkedIn', icon: Linkedin, id: 'linkedin' },
                { name: 'X', icon: Twitter, id: 'twitter' },
                { name: 'Instagram', icon: Instagram, id: 'instagram' },
                { name: 'Facebook', icon: Facebook, id: 'facebook', href: 'https://www.facebook.com/share/1DakTVbH3C/?mibextid=wwXIfr' },
                { name: 'YouTube', icon: Youtube, id: 'youtube' },
                { name: 'Telegram', icon: Send, id: 'telegram' },
              ].map((s) => {
                const external = s.href && s.href !== '#';
                return (
                  <a
                    key={s.id}
                    href={s.href || '#'}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                    aria-label={s.name}
                    title={s.name}
                    data-testid={`footer-social-${s.id}`}
                    className="p-2 -m-1 text-slate-600 hover:text-teal-700 transition-colors duration-200"
                  >
                    <s.icon className="w-5 h-5" strokeWidth={1.75} />
                  </a>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">256-bit encryption · PIPEDA compliant</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
